import { GameState, GameAction, CardDef, PlayerSide } from '../types.js';
import { getPlayerState, getOpponentSide, getInstance, getCardDef, getEffectivePower } from '../engine/GameState.js';
import { canAfford } from '../engine/SpiritManager.js';
import { getRalliesPerTurn, getSupportsPerTurn, getMaxHandSize } from '../engine/AbilityProcessor.js';
import { getLegalActions } from '../engine/Rules.js';
import { nextFloat } from '../engine/PRNG.js';

export function getAIAction(
  state: GameState,
  catalog: Map<string, CardDef>,
  side: PlayerSide,
): GameAction | null {
  const legal = getLegalActions(state, catalog);
  if (legal.length === 0) return null;

  switch (state.phase) {
    case 'main':
      return pickMainAction(state, legal, catalog, side);
    case 'attack':
      return pickAttackAction(state, legal, catalog, side);
    case 'counter':
      return pickCounterAction(state, legal, catalog, side);
    case 'cleanup':
      return pickDiscardAction(state, legal, catalog, side);
    default:
      return null;
  }
}

function pickMainAction(
  state: GameState,
  actions: GameAction[],
  catalog: Map<string, CardDef>,
  side: PlayerSide,
): GameAction {
  const ps = getPlayerState(state, side);

  // Priority 1: Play a character if we have an empty slot
  const playCharActions = actions
    .filter((a): a is GameAction & { type: 'PLAY_CHARACTER' } => a.type === 'PLAY_CHARACTER')
    .sort((a, b) => {
      const aInst = getInstance(state, a.instanceId);
      const bInst = getInstance(state, b.instanceId);
      const aDef = getCardDef(aInst.cardId, catalog);
      const bDef = getCardDef(bInst.cardId, catalog);
      if (aDef.type !== 'character' || bDef.type !== 'character') return 0;
      return bDef.power - aDef.power; // highest power first
    });

  if (playCharActions.length > 0) {
    return playCharActions[0];
  }

  // Priority 2: Support weakest ring character
  const supportActions = actions.filter(
    (a): a is GameAction & { type: 'SUPPORT_CHARACTER' } => a.type === 'SUPPORT_CHARACTER'
  );
  if (supportActions.length > 0) {
    supportActions.sort((a, b) => {
      return getEffectivePower(state, a.ringInstanceId, catalog) - getEffectivePower(state, b.ringInstanceId, catalog);
    });
    return supportActions[0];
  }

  // Priority 3: Use activated abilities (75% chance — sometimes save for attack/block).
  // Uses the state's seeded PRNG so the AI is replay-deterministic.
  const activateActions = actions.filter(a => a.type === 'ACTIVATE_ABILITY');
  if (activateActions.length > 0 && nextFloat(state.rng) < 0.75) {
    return activateActions[0];
  }

  // Priority 4: Play action cards — but only those that would actually
  // do something. The basic AI doesn't strategize over action cards, so
  // without this filter it'll happily Heal at full HP, Tactical Strike
  // when the player has no characters, Rallying Cry with nothing playable
  // in hand, etc. The predicate below is "would this card have a visible
  // effect at all," not "would it be net-positive" — the AI may still
  // play strategically dubious cards, but it won't burn turns on plays
  // that read as bugs to a watching player.
  const actionCardActions = actions.filter(
    (a): a is GameAction & { type: 'PLAY_ACTION' } => a.type === 'PLAY_ACTION',
  );
  for (const a of actionCardActions) {
    const def = getCardDef(getInstance(state, a.instanceId).cardId, catalog);
    if (def.type !== 'action') continue;
    if (wouldActionDoSomething(state, def, side, catalog)) return a;
  }

  // End main phase
  return { type: 'SKIP_PHASE' };
}

/** True if playing this action card would cause something visible to
 *  happen given the current state. Mirrors the targeting / atomic-effect
 *  logic in AbilityProcessor without dispatching: we only want to know
 *  whether the resolved effect would be a no-op.
 *
 *  For ally-targeting verb effects we explicitly require at least one
 *  ring character on the AI's side. selectTargets falls back to the
 *  source instance for `weakest-ally` / `strongest-ally` when no other
 *  allies exist, but for an action card the "source" is the card's own
 *  instance — already on its way to discard — so the boost / armor /
 *  heal would silently apply to nothing the player can see. Same goes
 *  for `all-allies` / `all-other-allies` and the `self-and-enemy`
 *  composites: without a real ring presence they're wasted. */
function wouldActionDoSomething(
  state: GameState,
  def: CardDef & { type: 'action' },
  side: PlayerSide,
  catalog: Map<string, CardDef>,
): boolean {
  const ps = getPlayerState(state, side);
  const ops = getPlayerState(state, getOpponentSide(side));
  const hasOwnAlly = ps.ring.some(s => s.instanceId);
  const hasEnemy = ops.ring.some(s => s.instanceId);

  // Atomic (no-target) effects.
  switch (def.effect) {
    case 'draw':
    case 'draw-and-spirit': {
      // Useful only if there's room in the hand AND a card to actually
      // pull from the deck. A draw at max hand + the draw event firing
      // produces no visible card-arrival.
      const maxHand = getMaxHandSize(state, side, catalog);
      return ps.hand.length < maxHand && ps.deck.length > 0;
    }
    case 'heal':
      return ps.hp < state.maxHp;
    case 'damage-opponent':
      // Always useful while the game is live (game-over check filters
      // earlier). Including for completeness so we don't fall through.
      return true;
    case 'spirit-gain':
      return true;
    case 'steal-spirit':
    case 'drain-spirit':
      return ops.spirit > 0;
    case 'discard-enemy-hand':
      return ops.hand.length > 0;
    case 'mill-enemy':
      return ops.deck.length > 0;
    case 'advance-clock':
    case 'rewind-clock':
      // Clock manipulation is strategic — the AI may want to enable a
      // bigger card next turn, or stall an opponent's clock-N play.
      // Hard to evaluate cheaply; allow.
      return true;
    case 'grant-extra-rally': {
      // Only useful if the bonus actually unlocks an additional play —
      // i.e., the AI has at least 2 playable characters in hand and at
      // least 2 empty ring slots. Otherwise the +1 to the cap goes
      // unused.
      const cap = getRalliesPerTurn(state, side, catalog);
      const emptySlots = ps.ring.filter(s => !s.instanceId).length;
      const playable = ps.hand.filter(id => {
        const inst = getInstance(state, id);
        const d = getCardDef(inst.cardId, catalog);
        if (d.type !== 'character') return false;
        if ((d.clock ?? 1) > Math.max(state.clock, 1)) return false;
        if (d.spirit < 0 && !canAfford(ps.spirit, Math.abs(d.spirit))) return false;
        return true;
      }).length;
      // The bonus enables a play only when the AI would otherwise hit
      // the cap. cap+1 plays needed for the bonus to matter.
      return emptySlots >= cap + 1 && playable >= cap + 1;
    }
    case 'grant-extra-support': {
      // Useful only if the AI can plausibly support twice this turn:
      // at least one ring character to receive support and at least
      // (cap+1) hand characters to spend. Tag-overlap rules are
      // skipped; the basic AI's filter shouldn't second-guess the
      // engine's legality check on the actual SUPPORT_CHARACTER step.
      const cap = getSupportsPerTurn(state, side, catalog);
      const handChars = ps.hand.filter(id => {
        const inst = getInstance(state, id);
        return getCardDef(inst.cardId, catalog).type === 'character';
      }).length;
      return hasOwnAlly && handChars >= cap + 1;
    }
  }

  // Verb+target effects (boost / armor / damage / destroy / slay /
  // ready / return). If `target` is missing the action is malformed;
  // play it and let the engine no-op.
  const target = def.target;
  if (!target) return true;

  // `slay` only KO's targets whose effective Power >= intensity. Playing
  // it against a board with no qualifying character is wasted, so check
  // the threshold against the side(s) the target draws from before the
  // generic presence checks below. The active-player tie-bias on
  // `strongest-character` doesn't affect this: we just need *any*
  // candidate to clear the bar on the side(s) the target picks from.
  if (def.effect === 'slay') {
    const threshold = def.intensity ?? 0;
    const sidesForTarget: PlayerSide[] = [];
    if (target === 'weakest-ally' || target === 'strongest-ally'
        || target === 'all-allies' || target === 'all-other-allies') {
      sidesForTarget.push(side);
    } else if (target === 'weakest-enemy' || target === 'strongest-enemy'
        || target === 'all-enemies'
        || target === 'self-and-weakest-enemy' || target === 'self-and-strongest-enemy') {
      sidesForTarget.push(getOpponentSide(side));
    } else if (target === 'all-characters' || target === 'strongest-character'
        || target === 'weakest-character') {
      sidesForTarget.push(side, getOpponentSide(side));
    }
    const hasViableTarget = sidesForTarget.some(s =>
      getPlayerState(state, s).ring.some(slot => {
        if (!slot.instanceId) return false;
        return getEffectivePower(state, slot.instanceId, catalog) >= threshold;
      }),
    );
    if (!hasViableTarget) return false;
  }

  switch (target) {
    case 'self':
      // Action cards have no board source; targeting "self" means the
      // card's own instance, already on its way to discard. Treat as
      // no-op. (No current action card actually uses self-target, but
      // including for completeness.)
      return false;
    case 'weakest-ally':
    case 'strongest-ally':
    case 'all-allies':
    case 'all-other-allies':
      return hasOwnAlly;
    case 'weakest-enemy':
    case 'strongest-enemy':
    case 'all-enemies':
      return hasEnemy;
    case 'self-and-weakest-enemy':
    case 'self-and-strongest-enemy':
      // Same source-fallback caveat as ally targets; the "self" half
      // resolves to the action card itself (wasted) so we need a real
      // enemy on the board for the effect to land somewhere visible.
      return hasEnemy;
    case 'all-characters':
    case 'strongest-character':
    case 'weakest-character':
      // Cross-side picks land somewhere as long as *any* character is
      // on the board. The active-player-wins-ties rule biases toward
      // the favorable side, but the action isn't a no-op either way
      // when there's at least one valid candidate.
      return hasOwnAlly || hasEnemy;
  }
}

function pickAttackAction(
  state: GameState,
  actions: GameAction[],
  catalog: Map<string, CardDef>,
  side: PlayerSide,
): GameAction {
  const ps = getPlayerState(state, side);
  const opSide = getOpponentSide(side);
  const ops = getPlayerState(state, opSide);

  const attackActions = actions.filter(
    (a): a is GameAction & { type: 'DECLARE_ATTACKERS' } => a.type === 'DECLARE_ATTACKERS'
  );

  if (attackActions.length === 0) {
    return { type: 'SKIP_PHASE' };
  }

  // Helper: get total attacker count for an action
  const totalAttackers = (a: GameAction & { type: 'DECLARE_ATTACKERS' }) =>
    a.groups.reduce((n, g) => n + g.attackerIds.length, 0);

  // Helper: get all attacker IDs
  const allAttackerIds = (a: GameAction & { type: 'DECLARE_ATTACKERS' }) =>
    a.groups.flatMap(g => g.attackerIds);

  // Calculate total opponent ring power
  let opponentTotalPower = 0;
  let opponentCharCount = 0;
  for (const slot of ops.ring) {
    if (slot.instanceId) {
      const inst = getInstance(state, slot.instanceId);
      if (!inst.isExhausted) {
        opponentTotalPower += getEffectivePower(state, slot.instanceId, catalog);
        opponentCharCount++;
      }
    }
  }

  // Calculate our total eligible attack power
  let ourTotalPower = 0;
  const eligibleIds: string[] = [];
  for (const slot of ps.ring) {
    if (slot.instanceId) {
      const inst = getInstance(state, slot.instanceId);
      if (!inst.isFresh && !inst.isExhausted) {
        ourTotalPower += getEffectivePower(state, slot.instanceId, catalog);
        eligibleIds.push(slot.instanceId);
      }
    }
  }

  // Strategy: If we overwhelm, attack with all individually
  if (eligibleIds.length > 0 && ourTotalPower > opponentTotalPower * 1.2) {
    const allAttack = attackActions.find(
      a => totalAttackers(a) === eligibleIds.length && a.groups.every(g => !g.united)
    );
    if (allAttack) return allAttack;
  }

  // If opponent has no ready characters, attack with all
  if (opponentCharCount === 0 && eligibleIds.length > 0) {
    const allAttack = attackActions.find(
      a => totalAttackers(a) === eligibleIds.length && a.groups.every(g => !g.united)
    );
    if (allAttack) return allAttack;
    // Fallback: strongest single
    const sorted = [...eligibleIds].sort((a, b) =>
      getEffectivePower(state, b, catalog) - getEffectivePower(state, a, catalog)
    );
    const singleAttack = attackActions.find(
      a => totalAttackers(a) === 1 && allAttackerIds(a)[0] === sorted[0]
    );
    if (singleAttack) return singleAttack;
  }

  // Try united attack if we have enough spirit
  const unitedAttacks = attackActions.filter(a => a.groups.some(g => g.united));
  if (unitedAttacks.length > 0) {
    // Pick the biggest united group
    const sorted = unitedAttacks.sort((a, b) => {
      const aMax = Math.max(...a.groups.filter(g => g.united).map(g => g.attackerIds.length));
      const bMax = Math.max(...b.groups.filter(g => g.united).map(g => g.attackerIds.length));
      return bMax - aMax;
    });
    const best = sorted[0];
    const unitedGroup = best.groups.find(g => g.united)!;
    const combinedPower = unitedGroup.attackerIds.reduce(
      (sum, id) => sum + getEffectivePower(state, id, catalog), 0
    );
    let strongestOpponent = 0;
    for (const slot of ops.ring) {
      if (slot.instanceId) {
        strongestOpponent = Math.max(strongestOpponent, getEffectivePower(state, slot.instanceId, catalog));
      }
    }
    if (combinedPower > strongestOpponent + 300) {
      return best;
    }
  }

  // Attack with strongest single character
  if (eligibleIds.length > 0) {
    const sorted = [...eligibleIds].sort((a, b) =>
      getEffectivePower(state, b, catalog) - getEffectivePower(state, a, catalog)
    );
    const strongest = sorted[0];
    const strongestPower = getEffectivePower(state, strongest, catalog);

    if (strongestPower >= 400 || opponentCharCount === 0) {
      const singleAttack = attackActions.find(
        a => totalAttackers(a) === 1 && allAttackerIds(a)[0] === strongest
      );
      if (singleAttack) return singleAttack;
    }
  }

  return { type: 'SKIP_PHASE' };
}

function pickCounterAction(
  state: GameState,
  actions: GameAction[],
  catalog: Map<string, CardDef>,
  side: PlayerSide,
): GameAction {
  if (!state.combatState) {
    return { type: 'CONFIRM_BLOCKERS' };
  }

  // First, try to play reaction cards
  const reactionActions = actions.filter(a => a.type === 'PLAY_REACTION');
  if (reactionActions.length > 0) {
    return reactionActions[0];
  }

  // Assign blockers
  const blockerActions = actions.filter(
    (a): a is GameAction & { type: 'ASSIGN_BLOCKER' } => a.type === 'ASSIGN_BLOCKER'
  );

  if (blockerActions.length > 0) {
    // Calculate effective power per group
    const groupPower = (gIdx: number): number => {
      const group = state.combatState!.groups[gIdx];
      return group.attackerIds.reduce((sum, id) => sum + getEffectivePower(state, id, catalog), 0);
    };

    // Sort by strongest group first
    const sorted = [...blockerActions].sort((a, b) => groupPower(b.groupIndex) - groupPower(a.groupIndex));

    for (const action of sorted) {
      const attackPower = groupPower(action.groupIndex);
      const blockerPower = getEffectivePower(state, action.blockerInstanceId, catalog);

      // Block if we can survive or trade
      if (blockerPower >= attackPower) {
        return action;
      }
      // Block big attacks even if sacrificing
      if (attackPower >= 600) {
        return action;
      }
    }

    // If strongest group > 400, sacrifice weakest blocker
    const strongestGroup = sorted[0];
    if (groupPower(strongestGroup.groupIndex) > 400) {
      const cheapest = blockerActions
        .filter(a => a.groupIndex === strongestGroup.groupIndex)
        .sort((a, b) =>
          getEffectivePower(state, a.blockerInstanceId, catalog) -
          getEffectivePower(state, b.blockerInstanceId, catalog)
        );
      if (cheapest.length > 0) return cheapest[0];
    }
  }

  return { type: 'CONFIRM_BLOCKERS' };
}

function pickDiscardAction(
  state: GameState,
  actions: GameAction[],
  catalog: Map<string, CardDef>,
  side: PlayerSide,
): GameAction | null {
  const discardActions = actions.filter(
    (a): a is GameAction & { type: 'DISCARD_CARD' } => a.type === 'DISCARD_CARD'
  );

  if (discardActions.length === 0) return null;

  // Discard the weakest/least useful card:
  // Prefer discarding low-power characters and high-cost actions
  discardActions.sort((a, b) => {
    const aInst = getInstance(state, a.instanceId);
    const bInst = getInstance(state, b.instanceId);
    const aDef = getCardDef(aInst.cardId, catalog);
    const bDef = getCardDef(bInst.cardId, catalog);

    // Score: higher = more likely to discard
    const score = (def: CardDef): number => {
      if (def.type === 'action') {
        // Discard expensive actions first
        return def.spirit < 0 ? Math.abs(def.spirit) * 10 : 0;
      }
      // Discard low-power characters first
      return 1000 - def.power;
    };

    return score(bDef) - score(aDef);
  });

  return discardActions[0];
}
